// Fiverr context params on every link Mira hands a CLIENT into fiverr.com.
//
// WHY CONTEXT PARAMS AND NOT UTMs. Mira is a Fiverr surface, not a marketing
// channel, so it tags its links the way Fiverr's own surfaces do (search,
// recommendations, order emails): `context_referrer` + `ref_ctx_id`. Fiverr's
// page view plugin reads both on every Perseus page and writes them, server
// side and for logged-out visitors too, to `page.referrer.name` /
// `page.referrer.ctx_id` in `raw.events`, next to the visitor's `uid` (verified
// on prod data 2026-10-12). Marketing never sees them: its channel comes from
// `utm_*` and the HTTP referrer only, and any `utm_source` counts as PAID
// traffic that takes the visitor's channel for 30 days. That is what the UTMs
// this module used to add did to Fiverr's attribution models.
//
// UTM PASS-THROUGH. Mira never INVENTS a UTM. But a visit that a Google paid
// campaign brought to Mira (`?utm_source=google&utm_medium=cpc&utm_campaign=...`)
// is that campaign's visitor, and the campaign must keep the credit when the
// client continues to fiverr.com. So every link this module decorates also
// carries the visit's OWN landing UTMs, verbatim, but only for a Google CPC
// campaign (`utm_source=google` with `utm_medium=cpc` or `cpc-<kind>`, e.g.
// `cpc-brand`; see `passesThrough`). ANY OTHER campaign is captured for Mira's
// funnel but never forwarded: an email, an AI assistant's
// `utm_source=chatgpt.com`, an affiliate, a support link. Fiverr counts any
// `utm_source` as paid traffic, so forwarding those would report them to Fiverr
// as paid. The UTMs come from `setMiraLinkUtm` (the buyer SPA registers `currentUtm`: the five standard `utm_*` the visit landed with,
// sticky in sessionStorage across the Last Mile handoff and the sign-in round
// trip, last campaign wins). A visit that landed with none adds none, so Mira's
// own traffic stays unpaid. A URL that already carries any `utm_*` keeps its own
// campaign: we never mix two.
//
// THE TWO VALUES.
//  - `context_referrer` = `mira_<surface>`, a bounded vocabulary (below).
//  - `ref_ctx_id` = the link context id. In the buyer SPA that is the funnel
//    `visit_id` (registered at boot through `setMiraLinkContextId`), which every
//    Mira funnel event already carries next to `project_id`, so a Fiverr page
//    view joins back to the Mira project with no new storage. Anywhere nothing
//    is registered (the MCP card), it is a random id minted once per page
//    load, in memory. It is ALWAYS present: Fiverr's Rails pages drop
//    `context_referrer` when `ref_ctx_id` is missing.
//
// WHERE IT RUNS. At the LAST HOP, never in stored data. A talent's
// `fiverrProfileUrl` is DATA: the backend stamps it, snapshots persist it,
// `fiverr-inbox.ts` parses it for the handle and the origin, and the handoff
// export ships it to BigQuery. Decorating THAT would poison every one of those
// readers. This module decorates where the href is RENDERED or the tab is
// NAVIGATED, and nowhere else.
//
// The backend half lives in `recruiter/fiverr_web.py::with_mira_context` (same
// params, same host check, same never-overwrite rule, and no pass-through,
// since it has no visit). It covers only a link
// with NO client hop, which today is one: the static maintenance page. The
// canned notices in `attention.py` are prose we persist and stream, and the
// renderer uses the URL as its own link text, so params baked in upstream would
// be visible copy. Those get tagged at render instead, on `agent_prose`.

/** The prefix of every `context_referrer` value Mira writes. */
export const MIRA_CONTEXT_PREFIX = "mira";

/**
 * The Mira SURFACE the link lives on, sent as `context_referrer=mira_<surface>`.
 *
 * A bounded enum on purpose: these values land in Fiverr's `page.referrer.name`,
 * so the vocabulary has to stay small enough to group by. NEVER encode an id
 * here: the id rides `ref_ctx_id`.
 */
export type FiverrLinkSurface =
  | "results_card" // the shortlist deck's talent cards
  | "results_finalist" // the results compare table
  | "results_self_search" // the results panel's "search Fiverr myself" escape
  | "finalist_pick" // the concierge finalist-pick offer cards
  | "live_matches_rail" // the definition-phase live rail
  | "field_seller_card" // the Mira-field talent card
  | "field_chat" // the Mira-field handoff chat's "Open in Fiverr"
  | "project_room" // the client's project room after an order: the order pages
  | "field_dock" // the Mira-field took-over dock's faces, inside the Fiverr app
  | "dashboard_dead_end" // the waiting board's dead-end popup
  | "talent_engaged" // the post-pick "talent engaged" screen
  | "workspace_pick" // the workspace's pick handler (pre-opened tab)
  | "brief_push" // the take-control brief push (pre-opened tab)
  | "search_fallback" // the no-match terminal screen
  | "handoff_stage" // the concierge handoff screen
  | "user_menu" // the user menu's Help Center link
  | "home_footer" // the homepage footer's links (Help Center, Submit a request, fiverr.com)
  | "home_faq" // links inside the homepage "About Mira and FAQ" answers
  | "home_project_card" // a talent's face on a home page project card (Monday 3323686577)
  | "handoff_scheduling" // the specialist-call booking page
  | "mcp_card" // the Mira card rendered inside Claude / ChatGPT
  | "agent_prose" // a link Mira wrote into her own reply
  | "route_search"; // the tailored Fiverr search card (the marketplace_route A/B)

/** The params Fiverr reads as a link's context. A URL carrying either is left alone. */
const CONTEXT_KEYS = new Set(["context_referrer", "ref_ctx_id"]);

function randomId(): string {
  try {
    return globalThis.crypto.randomUUID();
  } catch {
    const bytes = new Uint32Array(4);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(8, "0")).join("");
  }
}

let pageLoadId: string | undefined;
let contextIdProvider: (() => string) | undefined;

/**
 * Register where `ref_ctx_id` comes from. The buyer SPA registers the funnel
 * `visit_id` at boot, before the first render. Kept as a hook rather than an
 * import so `mcp-ui` can use this module without the analytics beacon.
 */
export function setMiraLinkContextId(provider: () => string): void {
  contextIdProvider = provider;
}

/** The `ref_ctx_id` for a link built now. Never empty. */
export function miraLinkContextId(): string {
  let id = "";
  try {
    id = contextIdProvider?.() ?? "";
  } catch {
    // A failing provider must not cost the link its context; fall through.
  }
  if (id) return id;
  pageLoadId ??= randomId();
  return pageLoadId;
}

/** The campaign params passed through, in the order they are appended. */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/** A visit's landing campaign, as the pass-through reads it. Structurally the
 * analytics `Utm`, declared here so this module never imports the beacon. */
export type MiraLinkUtm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

let utmProvider: (() => MiraLinkUtm | undefined) | undefined;

/**
 * Register where the pass-through UTMs come from. The buyer SPA registers the
 * visit's landing campaign (`currentUtm`) at boot. Nothing registered (the MCP
 * card, the talent app) means no pass-through.
 */
export function setMiraLinkUtm(provider: () => MiraLinkUtm | undefined): void {
  utmProvider = provider;
}

/** `cpc`, or a kind of it (`cpc-brand`, `cpc_nonbrand`). */
const CPC_MEDIUM = /^cpc(?:[-_].*)?$/;

/**
 * Whether the visit's campaign may travel on to fiverr.com: Google CPC only.
 * Compared trimmed and case-insensitive, since ad platforms and hand-written
 * links disagree on case. Anything else stays Mira's own measurement (see the
 * top of this module).
 */
function passesThrough(utm: MiraLinkUtm): boolean {
  const source = utm.utm_source?.trim().toLowerCase() ?? "";
  const medium = utm.utm_medium?.trim().toLowerCase() ?? "";
  return source === "google" && CPC_MEDIUM.test(medium);
}

/** The `&utm_*=...` suffix for a link to `parsed`, or "" when the visit has no
 * campaign, its campaign is not one that passes through, or the URL already
 * carries a `utm_*` of its own. */
function passThroughUtm(parsed: URL): string {
  for (const key of parsed.searchParams.keys()) {
    if (key.toLowerCase().startsWith("utm_")) return "";
  }
  let utm: MiraLinkUtm | undefined;
  try {
    utm = utmProvider?.();
  } catch {
    return ""; // a failing provider costs the link its campaign, never the link
  }
  if (!utm || !passesThrough(utm)) return "";
  let out = "";
  for (const key of UTM_KEYS) {
    const value = utm[key];
    if (typeof value === "string" && value) out += `&${key}=${encodeURIComponent(value)}`;
  }
  return out;
}

/**
 * Exact-host match, never a substring: `evilfiverr.com` and
 * `fiverr.com.evil.io` must NOT be decorated, while `www.`, `www.dev.`,
 * `help.` and `propages.` must.
 */
function isFiverrHost(hostname: string): boolean {
  return hostname === "fiverr.com" || hostname.endsWith(".fiverr.com");
}

/**
 * Adds Mira's context params to a fiverr.com URL, plus the visit's own landing
 * UTMs when they are a Google CPC campaign (the pass-through, see the top of
 * this module).
 *
 * The IDENTITY function when `url` is not an absolute URL, when the host is not
 * fiverr.com / *.fiverr.com, or when the URL already carries `context_referrer`
 * or `ref_ctx_id`. A URL that arrives with a context belongs to whatever put it
 * there (a Fiverr search link, a Fiverr email), and overwriting it would move
 * that surface's credit onto Mira.
 *
 * Existing query params and the hash are preserved BYTE for byte - the original
 * string is appended to rather than re-serialized through `URLSearchParams`,
 * which would rewrite `%20` as `+` and normalize the path.
 */
export function withMiraContext(url: string, surface: FiverrLinkSurface): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url; // relative, empty, or garbage - nothing to decorate
  }
  if (!isFiverrHost(parsed.hostname)) return url;
  for (const key of parsed.searchParams.keys()) {
    if (CONTEXT_KEYS.has(key.toLowerCase())) return url;
  }
  const hashAt = url.indexOf("#");
  const head = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  // A "?" that is already there but EMPTY ("…/x?") takes no separator at all.
  const sep = head.endsWith("?") ? "" : head.includes("?") ? "&" : "?";
  const params =
    `context_referrer=${MIRA_CONTEXT_PREFIX}_${surface}` +
    `&ref_ctx_id=${encodeURIComponent(miraLinkContextId())}` +
    passThroughUtm(parsed);
  return `${head}${sep}${params}${hash}`;
}

// IN THE FIVERR APP, A LINK OPENS IN THIS WINDOW (Monday 3296410195).
//
// The Fiverr apps show Mira in a webview, and they open a NATIVE screen (the
// profile, an order, home, a gig, search, a conversation) only for a navigation
// they catch in that same webview. A new window or tab escapes it: the page
// opens in a bare browser view, and Back no longer returns to Mira (Monday
// 3250082493). So inside the app every fiverr.com link navigates in place; the
// app intercepts it, pushes its native screen, and Back pops back to the Mira
// page, still loaded. A Fiverr page the app does not claim (the help center,
// any non-www host) simply loads in the webview.
//
// Every OTHER http(s) page navigates in place too. Read from the apps' own code
// (2026-10-21): both load a non-Fiverr host in the Mira webview and never hand
// one to an outside browser (iOS `WebViewController` allows the navigation,
// Android's `WebViewScreenViewModel` returns false from its override). They
// load a `_blank` link in place as well (iOS loads a `targetFrame == nil`
// action into the same webview; Android registers no `onCreateWindow`). But a
// `window.open` that no tap started is dropped on iOS (the webview never sets
// `javaScriptCanOpenWindowsAutomatically`), and iOS's `window.open` returns
// null even when the page did load, so a new window is never something to
// count on there. Two Android quirks this does not work around: a `zoom.us`
// host closes the whole Mira screen, and a scheme other than http(s), `mailto:`
// and `market:` is dropped. A non-http(s) URL keeps the browser behavior here.
//
// A browser keeps opening a new tab, exactly as before. The new windows that
// stay out of this rule by plan decision (the booking pages, Mira's own sign-in
// popup) do not go through it at all: they carry `new-window-allow`
// (`web/src/lib/new-window-coverage.test.ts`).
//
// WHO DECIDES "IN THE APP". Not this package: the buyer SPA's `hostApp()` is the
// one detector (`web/src/lib/host-app.ts`), and `shared` may not import an app.
// The SPA registers it at boot through `setFiverrLinksInPlace`, the same way it
// registers the link context id above. Nothing registered (the MCP card, the
// seller SPA, a test) means a browser.

let inPlaceProvider: (() => boolean) | undefined;

/** Register whether this tab runs inside a Fiverr app webview. The buyer SPA
 * registers `() => hostApp() !== null` at boot, before the first render. */
export function setFiverrLinksInPlace(provider: () => boolean): void {
  inPlaceProvider = provider;
}

/** True when `url` must open in THIS window: any absolute http(s) URL, a
 * fiverr.com page or not, inside a Fiverr app. False in a browser, and false
 * for a relative, non-http(s) or unparseable URL. */
export function opensInPlace(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (!/^https?:$/.test(parsed.protocol)) return false;
  try {
    return inPlaceProvider?.() === true;
  } catch {
    return false; // a failing provider leaves the link as a browser has it
  }
}

/** The `target` and `rel` for an outbound `<a>`: spread them onto the anchor.
 * A new tab with `rel` (pass the site's own, so a browser's link is unchanged),
 * or nothing at all for an http(s) page inside the app, which navigates in place. */
export function outboundLinkProps(
  href: string,
  rel = "noopener noreferrer",
): { target?: "_blank"; rel?: string } {
  return opensInPlace(href) ? {} : { target: "_blank", rel };
}
